import { buildApiUrl, buildAssetUrl, withBusinessHeaders } from './apiConfig';

const EXPENSES_API_URL = process.env.REACT_APP_EXPENSES_API_URL || buildApiUrl('/expenses');

export async function fetchExpensesDashboard(filters = {}) {
  const query = new URLSearchParams();

  if (filters.staffId) {
    query.set('staffId', filters.staffId);
  }

  if (filters.status && filters.status !== 'all') {
    query.set('status', filters.status);
  }

  if (filters.date) {
    query.set('date', filters.date);
  }

  if (filters.year) {
    query.set('year', filters.year);
  }

  if (filters.search) {
    query.set('search', filters.search);
  }

  const url = query.toString() ? `${EXPENSES_API_URL}?${query.toString()}` : EXPENSES_API_URL;
  const response = await requestExpenseApi(url, {
    fallbackMessage: 'Unable to load expenses right now.',
  });
  const payload = await response.json();

  return {
    cashAdvances: (payload.cashAdvances || []).map(normalizeCashAdvance),
    expenses: (payload.expenses || []).map(normalizeExpense),
    staffOptions: payload.staffOptions || [],
    categories: payload.categories || [],
    summary: payload.summary || {},
  };
}

export async function createStaffCashAdvance(payload) {
  const response = await requestExpenseApi(`${EXPENSES_API_URL}/cash-advances`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to save staff cash advance right now.',
  });

  return normalizeCashAdvance(await response.json());
}

export async function updateStaffCashAdvance(id, payload) {
  const response = await requestExpenseApi(`${EXPENSES_API_URL}/cash-advances/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to update staff cash advance right now.',
  });

  return normalizeCashAdvance(await response.json());
}

export async function deleteStaffCashAdvance(id) {
  const response = await requestExpenseApi(`${EXPENSES_API_URL}/cash-advances/${id}`, {
    method: 'DELETE',
    fallbackMessage: 'Unable to delete staff cash advance right now.',
  });

  return normalizeCashAdvance(await response.json());
}

export async function createStaffExpense(payload) {
  const response = await requestExpenseApi(`${EXPENSES_API_URL}/expenses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to submit staff expense right now.',
  });

  return normalizeExpense(await response.json());
}

export async function reviewStaffExpense(id, payload) {
  const response = await requestExpenseApi(`${EXPENSES_API_URL}/expenses/${id}/review`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to review staff expense right now.',
  });

  return normalizeExpense(await response.json());
}

export async function updateStaffExpense(id, payload) {
  const response = await requestExpenseApi(`${EXPENSES_API_URL}/expenses/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to update staff expense right now.',
  });

  return normalizeExpense(await response.json());
}

export async function deleteStaffExpense(id) {
  const response = await requestExpenseApi(`${EXPENSES_API_URL}/expenses/${id}`, {
    method: 'DELETE',
    fallbackMessage: 'Unable to delete staff expense right now.',
  });

  return normalizeExpense(await response.json());
}

function normalizeCashAdvance(record) {
  const proofPreview = record.proofPreview || (record.proofImage ? buildAssetUrl(record.proofImage) : '');

  return {
    ...record,
    amount: Number(record.amount || 0),
    approvedExpenseAmount: Number(record.approvedExpenseAmount || 0),
    pendingExpenseAmount: Number(record.pendingExpenseAmount || 0),
    remainingAmount: Number(record.remainingAmount || 0),
    projectedRemainingAmount: Number(record.projectedRemainingAmount || 0),
    proofImageDaysRemaining:
      record.proofImageDaysRemaining === null || record.proofImageDaysRemaining === undefined
        ? null
        : Number(record.proofImageDaysRemaining),
    proofPreview: record.isProofImageExpired ? '' : buildAssetUrl(proofPreview),
  };
}

function normalizeExpense(record) {
  const proofPreview = record.proofPreview || (record.proofImage ? buildAssetUrl(record.proofImage) : '');

  return {
    ...record,
    amount: Number(record.amount || 0),
    proofImageDaysRemaining:
      record.proofImageDaysRemaining === null || record.proofImageDaysRemaining === undefined
        ? null
        : Number(record.proofImageDaysRemaining),
    proofPreview: record.isProofImageExpired ? '' : buildAssetUrl(proofPreview),
  };
}

async function requestExpenseApi(url, options) {
  const { fallbackMessage, ...requestOptions } = options;
  let response;

  try {
    response = await fetch(url, {
      ...requestOptions,
      headers: withBusinessHeaders(requestOptions.headers),
    });
  } catch (error) {
    throw new Error(fallbackMessage);
  }

  if (!response.ok) {
    const errorPayload = await safeReadJson(response);
    throw new Error(errorPayload?.message || fallbackMessage);
  }

  return response;
}

async function safeReadJson(response) {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}
